<template>
    <v-container class="pa-5" fluid>
        <ComplaintReturnDialog
            :is-open="complaintReturnDialog"
            :current-complaint="currentComplaint"
            :action-loading="actionLoading"
            @on-confirm="updateComplaint"
            @on-close="closeComplaintReturnDialog"
        />
        <ComplaintReturnListDialog
            :is-open="complaintReturnListDialog"
            :current-complaint="currentComplaint"
            :action-loading="actionLoading"
            @on-confirm="updateComplaint"
            @on-close="closeComplaintReturnListDialog"
        />
        <page-title>{{ pageTitle }}</page-title>

        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" sm="4" md="4" lg="3">
                            <v-text-field
                                v-model="filters.complaintPackageNumber"
                                label="Numer listu przewozowego..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('complaintPackageNumber')"
                            />
                        </v-col>
                        <v-col cols="12" sm="4" md="4" lg="3" xl="2">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="complaintsHeaders"
                        :items="complaints"
                        :loading="complaintsLoading"
                        :footer-props="footerProps"
                        :server-items-length="pagination.itemsLength"
                        :page.sync="pagination.page"
                        :mobile-breakpoint="0"
                        name="complaintsData"
                        @pagination="updatePagination"
                    >
                        <template #item="{item}">
                            <tr>
                                <td>
                                    {{ item.complaintNumber }}
                                </td>
                                <td>
                                    <div v-for="(order, index) in parseValue(item.orderNumbers)" :key="index">
                                        {{ order }}
                                    </div>
                                </td>
                                <td>
                                    {{ item.complaintStatus.name }}
                                </td>
                                <td>
                                    {{ formatDate(item.createdAt) }}
                                </td>
                                <td>
                                    <div class="d-inline-flex">
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Dodaj list przewozowy"
                                                    v-on="on"
                                                    @click="showComplaintReturnDialog(item)"
                                                >
                                                    <v-icon color="blue">
                                                        mdi-plus
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Dodaj list przewozowy</span>
                                        </v-tooltip>
                                        <v-tooltip :open-delay="tooltipDelay" top>
                                            <template #activator="{on}">
                                                <v-btn
                                                    icon
                                                    data-sentry-label="Edytuj"
                                                    v-on="on"
                                                    @click="showComplaintReturnListDialog(item)"
                                                >
                                                    <v-icon color="teal">
                                                        mdi-pencil
                                                    </v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Edytuj</span>
                                        </v-tooltip>
                                    </div>
                                </td>
                            </tr>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import PageTitle from '@/components/common/PageTitle'
import filters from '@/mixins/dataTableFilters'
import pagination from '@/mixins/dataTablePagination'
import customFilters from '@/mixins/customFilters'
import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import ComplaintReturnDialog from './ComplaintReturnDialog'
import ComplaintReturnListDialog from './ComplaintReturnListDialog'
import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'StorageComplaintsList',
    components: {
        PageTitle, ComplaintReturnDialog, ComplaintReturnListDialog
    },
    mixins: [
        dateFormatter,
        tooltipDelay,
        filters,
        customFilters,
        pagination
    ],
    beforeRouteEnter(to, from, next) {
        next(vm => {
            vm.pageTitle = to.meta.title
        })
    },
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('complaints/clearComplaintsFilters')
        next()
    },
    data() {
        return {
            pageTitle: null,
            currentComplaint: {},
            confirmReturnToStorageDialog: false,
            actionLoading: false,
            complaintReturnDialog: false,
            complaintReturnListDialog: false,
            complaintsHeaders: [
                {
                    text: 'Numer reklamacji',
                    value: 'complaintNumber',
                    sortable: false
                },
                {
                    text: 'Numer zamówienia',
                    value: 'orderNumbers',
                    sortable: false
                },
                {
                    text: 'Status',
                    value: 'complaintStatus',
                    sortable: false,
                },
                {
                    text: 'Data złożenia',
                    value: 'createdAt',
                    sortable: false,
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 60
                }
            ],
        }
    },
    computed: {
        userId() {
            return this.$store.state.user.userId
        },
        complaints() {
            return this.$store.state.complaints.complaints.complaints
        },
        complaintsLoading() {
            return this.$store.state.complaints.complaints.complaintsLoading
        },
        savedFilters() {
            return this.$store.state.complaints.complaints.filters
        },
        filterQuery() {
            const filters = Object.assign({}, this.filters)
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        },
    },
    mounted() {
        this.$store.dispatch('administration/getUser', this.userId )
            .then(response => {
                this.user = response.data
                this.getComplaints()
            })
            .catch(error => {
                errorHandler(error)
            })
    },
    methods: {
        getComplaints() {
            this.$store.dispatch('complaints/getComplaints', {
                paginate: this.paginate(),
                query: `${this.filterQuery}&complaintStatus=3`
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        async getComplaint(complaint) {
            return await this.$store.dispatch('complaints/getComplaint', complaint.id)
                .catch(error => {
                    errorHandler(error)
                })
        },
        showComplaintReturnListDialog(complaint) {
            this.getComplaint(complaint).then(response => {
                this.currentComplaint = response.data
                this.complaintReturnListDialog = true
            })
        },
        closeComplaintReturnListDialog() {
            this.complaintReturnListDialog = false
            this.currentComplaint = {}
        },
        showComplaintReturnDialog(complaint) {
            this.getComplaint(complaint).then(response => {
                this.currentComplaint = response.data
                this.complaintReturnDialog = true
            })
        },
        closeComplaintReturnDialog() {
            this.complaintReturnDialog = false
            this.currentComplaint = {}
        },
        updateComplaint(complaint) {
            this.actionLoading = true

            delete complaint.complaintAttachment
            this.$store.dispatch('complaints/updateComplaint', complaint)
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading = false
                    this.closeComplaintReturnDialog()
                    this.closeComplaintReturnListDialog()
                    this.getComplaints()
                })
        },
        changeFilter() {
            this.resetPagination()
            this.getComplaints()
        },
        updatePagination(pagination) {
            this.pagination = pagination

            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getComplaints()
            }
            this.clearPaginationPending()
        },
        parseValue(value) {
            if (!value || !Array.isArray(value)) return []

            return value
        },
        formatPlannedReturnDate(date) {
            if (!date) {
                return '-'
            }

            return new Date(date).toLocaleDateString()
        },
    }
}
</script>